import ApiService from './api.service';

export type DashboardDateRange = {
  date_range?: string;
  custom_date_range_start?: string;
  custom_date_range_end?: string;
  user_timezone?: string;
  previous_period?: boolean;
};

export type DashboardFilterOption = {
  filter_type: string;
  description?: string;
  column_references?: any[];
  comparator?: string;
  query_list_node_id?: string;
  query_list_field_id?: string;
  static_list?: string[];
  allow_multiple?: boolean;
  default_value?: string | string[];
  dimension_id?: string;
  input_label?: string;
};

export type DashboardComponent = {
  id: string;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number;
  object_type?: string;
  object_id?: string;
  markdown_content?: string;
  filter_config?: DashboardFilterOption;
  column_whitelist?: string[];
  ignore_date_range?: boolean;
  hide_header?: boolean;
  hide_toolbar?: boolean;
  disable_sort?: boolean;
};

export type Dashboard = {
  id: string;
  name: string;
  description: string;
  components: DashboardComponent[];
  version_idx?: number;
  date_range_config?: DashboardDateRange;
  last_updated?: string;
  created_by?: string;
};

export type SingleRecordResponse<T> = {
  record: T;
};

export type ListRecordsResponse<T> = {
  records: T[];
  total_pages?: number;
  total_records?: number;
};

export class DashboardService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
  }): Promise<ListRecordsResponse<Dashboard>> {
    const {
      page = 1,
      pageSize = 50,
      filters = {},
    } = options || {};

    const queryParams: Record<string, any> = {
      page,
      limit: pageSize,
      ...filters,
    };

    const result = await ApiService.getInstance().request(
      'GET',
      '/dashboards',
      queryParams
    ) as ListRecordsResponse<Dashboard>;

    return result;
  }

  async findById(id: string): Promise<Dashboard> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/dashboards/${id}`
    ) as SingleRecordResponse<Dashboard>;
    return result.record;
  }

  async create(dashboard: Omit<Dashboard, 'id' | 'last_updated'>): Promise<Dashboard> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/dashboards',
      dashboard
    ) as SingleRecordResponse<Dashboard>;
    return result.record;
  }

  async update(id: string, updates: Partial<Dashboard>): Promise<Dashboard> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/dashboards/${id}`,
      updates
    ) as SingleRecordResponse<Dashboard>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/dashboards/${id}`
    );
  }
}

export const dashboardService = new DashboardService();

